// EstudianteCuestionarios — UI adaptativa por edad del estudiante.
// 5-10 → kids (caritas grandes), 11-15 → teen, 16-20 → young (chips),
// 20+ → adult (estilo formal original).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateMX } from "@/lib/dates";
import { clearAppCaches } from "@/lib/cacheBuster";
import { toast } from "sonner";
import { Mascot, FloatingOwl } from "@/components/OwlMascot";
import { speakOwl, stopOwl, isOwlVoiceOn, setOwlVoice, owlVoiceSupported, defaultOwlVoiceForStudents } from "@/lib/owlVoice";
import { Volume2, VolumeX } from "lucide-react";
import ImageInteractiveCanvas from "@/components/ImageInteractiveCanvas";
import StackedOperation from "@/components/StackedOperation";
import { getAgeTheme } from "@/lib/quizThemes";
import {
  PlayCircle, CheckCircle2, AlertCircle, ArrowRight, ArrowLeft,
  Sparkles, Star, PartyPopper, Rocket, ChevronRight, Lock,
} from "lucide-react";
import { RenderTextWithMath } from "@/components/MathFormulaEditor";
import StudentMathResponse from "@/components/StudentMathResponse";
import { TablePlayer } from "@/components/QuestionTable";

const STATUS = {
  pending:     { bg: "#FFF7DA", text: "#9C7A18", label: "Por hacer", icon: Star },
  in_progress: { bg: "#E8EEFB", text: "#2C49A0", label: "Continuar", icon: Rocket },
  completed:   { bg: "#E7F4E9", text: "#2A7A36", label: "Terminado", icon: CheckCircle2 },
  cancelled:   { bg: "#F2F4FA", text: "#6E7591", label: "Cancelado", icon: AlertCircle },
};

// ====================== Lista ======================
export function EstudianteCuestionarios() {
  const { user } = useAuth();
  const theme = useMemo(() => getAgeTheme(user?.age), [user?.age]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const location = useLocation();
  const justFinished = location.state?.justFinished;
  const scoreSummary = justFinished?.score_summary;
  const [showResult, setShowResult] = useState(!!scoreSummary);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/questionnaire-assignments/me");
      setAssignments(data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const pending = assignments.filter((a) => a.status === "pending" || a.status === "in_progress");
  const completed = assignments.filter((a) => a.status === "completed");

  const isAdult = theme.id === "adult";

  return (
    <div className="space-y-6" data-testid="est-cuestionarios">
      {showResult && scoreSummary && (
        <CadResultModal summary={scoreSummary} title={justFinished?.template_code} onClose={() => setShowResult(false)} />
      )}
      {/* Header adaptativo */}
      {isAdult ? (
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Tus diagnósticos</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547]">Mis diagnósticos CEP · CAD</h1>
          <p className="text-sm text-[#6E7591] mt-1">{theme.tone}</p>
        </div>
      ) : (
        <div className={`${theme.cardRadius} p-6 bg-gradient-to-br ${theme.headerGradient} ${theme.cardBorder} ${theme.cardShadow}`}>
          <h1 className={`font-display ${theme.id === "kids" ? "text-4xl md:text-5xl" : "text-3xl md:text-4xl"} ${theme.fontWeight} ${theme.id === "young" ? "text-white" : "text-[#1F2547]"} leading-tight`}>
            {theme.headerEmoji} {theme.headerTitle}
          </h1>
          <p className={`text-base mt-2 font-bold ${theme.id === "young" ? "text-white/90" : "text-[#1F2547]"}`}>
            {theme.tone}
          </p>
        </div>
      )}

      {/* Mascota (sólo si no es adult o con tamaño compacto en adult) */}
      {!isAdult && (
        <Mascot
          message="¡Hola! Soy Zarek 🦉. Cuando empieces tu diagnóstico, te acompañaré paso a paso."
          variant="info"
          testid="mascot-list"
          size={theme.mascotSize}
        />
      )}

      <div>
        <h2 className={`font-display text-2xl ${theme.fontWeight} text-[#1F2547] mb-3 flex items-center gap-2`}>
          {isAdult ? null : <Star size={20} className="text-[#FFCC28] fill-[#FFCC28]" />}
          Pendientes
        </h2>
        {loading && <p className="text-sm text-[#6E7591]">Cargando…</p>}
        {!loading && pending.length === 0 && (
          <div className={`${theme.cardRadius} border-2 border-dashed border-[#E1E5F0] bg-white p-8 text-center`}>
            {!isAdult && <p className="text-5xl mb-2">🎉</p>}
            <p className="font-bold text-[#1F2547]">No tienes diagnósticos pendientes.</p>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          {pending.map((a) => (
            <AssignmentCard key={a.id} assignment={a} theme={theme} onPlay={() => nav(`/estudiante/cuestionario/${a.id}`)} />
          ))}
        </div>
      </div>

      {completed.length > 0 && (
        <div>
          <h2 className={`font-display text-xl ${theme.fontWeight} text-[#1F2547] mt-6 mb-3 flex items-center gap-2`}>
            <CheckCircle2 size={18} className="text-[#3FA84D]" /> Terminados
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {completed.map((a) => (
              <div key={a.id} className="rounded-xl border-2 border-[#3FA84D]/40 bg-[#E7F4E9] p-4" data-testid={`done-card-${a.id}`}>
                <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#3FA84D]">Terminado</p>
                <p className="font-bold text-[#1F2547] mt-1">{a.template_title}</p>
                <p className="text-[11px] text-[#6E7591] mt-1">{a.completed_at ? new Date(a.completed_at).toLocaleString("es-ES") : "—"}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Resultado de autoevaluación CAD (#11) — barras verde/rojo de aciertos.
function CadResultModal({ summary, title, onClose }) {
  const total = summary.total || 0;
  const correct = summary.correct_count || 0;
  const incorrect = summary.incorrect_count || 0;
  const pct = summary.percentage ?? 0;
  const correctPct = total ? Math.round((correct / total) * 100) : 0;
  return (
    <div className="fixed inset-0 z-[70] bg-[#1F2547]/60 backdrop-blur-sm grid place-items-center p-4"
      data-testid="cad-result-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-6 text-center space-y-4">
          <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">Resultado · {title || "CAD"}</p>
          <h3 className="font-display text-2xl font-extrabold text-[#1F2547]">¡Diagnóstico enviado!</h3>
          <div className="text-5xl font-extrabold text-[#1F2547]" data-testid="cad-result-pct">{pct}%</div>
          <p className="text-sm text-[#4C5478]" data-testid="cad-result-fraction">
            {correct} de {total} correctas
          </p>
          <div className="h-4 rounded-full bg-[#FBE4EC] overflow-hidden flex">
            <div className="h-full bg-[#3FA84D]" style={{ width: `${correctPct}%` }} data-testid="cad-bar-correct" />
            <div className="h-full bg-[#A52258]" style={{ width: `${100 - correctPct}%` }} data-testid="cad-bar-incorrect" />
          </div>
          <div className="flex justify-center gap-4 text-sm font-bold">
            <span className="text-[#3FA84D]" data-testid="cad-correct-count">✓ {correct} correctas</span>
            <span className="text-[#A52258]" data-testid="cad-incorrect-count">✗ {incorrect} incorrectas</span>
          </div>
          <button onClick={onClose} className="jasq-btn-primary w-full mt-2" data-testid="cad-result-close">
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignmentCard({ assignment, theme, onPlay }) {
  const s = STATUS[assignment.status] || STATUS.pending;
  const Icon = s.icon;
  const isAdult = theme.id === "adult";
  const locked = !!assignment.locked;
  const availableLabel = formatDateMX(assignment.available_on || assignment.scheduled_for);
  return (
    <motion.div
      whileHover={isAdult || locked ? {} : { scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={`${theme.cardRadius} ${theme.cardBorder} ${theme.cardShadow} bg-white p-5 flex flex-col gap-3 ${locked ? "opacity-90" : ""}`}
      data-testid={`assignment-${assignment.id}`}
    >
      <div className="flex items-center justify-between">
        {locked ? (
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold bg-[#F2F4FA] text-[#6E7591]"
            data-testid={`locked-badge-${assignment.id}`}>
            <Lock size={12} /> Bloqueado
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold"
            style={{ background: s.bg, color: s.text }}>
            <Icon size={12} /> {s.label}
          </span>
        )}
        <span className="text-[10px] font-extrabold uppercase tracking-wider text-white bg-[#A43790] rounded-full px-2 py-0.5">
          {assignment.template_code}
        </span>
      </div>
      <div>
        <p className={`font-display ${theme.fontWeight} text-[#1F2547] ${isAdult ? "text-base" : "text-xl"} leading-tight`}>
          {assignment.template_title}
        </p>
        {assignment.scheduled_for && (
          <p className="text-[11px] text-[#6E7591] mt-1" data-testid={`date-${assignment.id}`}>📅 {formatDateMX(assignment.scheduled_for)}</p>
        )}
        {assignment.notes && <p className="text-xs text-[#4C5478] mt-2 italic">"{assignment.notes}"</p>}
      </div>
      {locked ? (
        <div
          data-testid={`locked-${assignment.id}`}
          className={`${isAdult ? "rounded-xl" : "rounded-2xl"} bg-[#F2F4FA] text-[#6E7591] font-bold px-4 py-3 inline-flex items-center justify-center gap-2 border-2 border-dashed border-[#CBD2E4] cursor-not-allowed`}
        >
          <Lock size={16} />
          {availableLabel ? `Disponible el ${availableLabel}` : "Aún no disponible"}
        </div>
      ) : (
        <button onClick={onPlay}
          data-testid={`play-${assignment.id}`}
          className={`${isAdult ? "jasq-btn-primary" : `rounded-2xl bg-gradient-to-r ${theme.primaryBtnGradient} text-white ${theme.fontWeight} px-4 py-3 inline-flex items-center justify-center gap-2 border-2 border-[#1F2547]`}`}>
          <PlayCircle size={18} />
          {assignment.status === "in_progress"
            ? (theme.id === "kids" ? "¡Sigamos!" : "Continuar")
            : theme.startLabel}
        </button>
      )}
    </motion.div>
  );
}


// Fondo interactivo de la evaluación (ilustración IA por escena + edad).
// Se adapta: niños = caricaturesco · adolescentes/jóvenes/adultos = realista.
function QuizBackground({ scene, theme }) {
  if (!scene || scene === "none") return null;
  const ageKey = theme.id === "kids" ? "kids" : "teen";
  const url = `/quiz-bg/${scene}-${ageKey}.png`;
  // Velo sutil: el texto siempre va sobre tarjetas blancas opacas, así que el
  // fondo puede lucir. Niños: velo claro · adolescentes+: velo oscuro (mood espacial).
  const scrim = theme.id === "kids" ? "bg-white/35" : "bg-[#0A0F24]/35";
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }} aria-hidden data-testid="quiz-bg">
      <motion.div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${url})` }}
        initial={{ scale: 1.06 }}
        animate={{ scale: 1.14 }}
        transition={{ duration: 34, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
      />
      <div className={`absolute inset-0 ${scrim}`} />
    </div>
  );
}


// ====================== Cuestionario interactivo ======================
// ---- v5.5.0 · borrador local + red de seguridad ante fallos de pantalla ----
const draftKey = (id) => `quiz_draft_${id}`;
function readDraft(id) {
  try { return JSON.parse(sessionStorage.getItem(draftKey(id)) || "null") || {}; } catch { return {}; }
}
function writeDraft(id, v) {
  try { sessionStorage.setItem(draftKey(id), JSON.stringify(v)); } catch { /* sin almacenamiento */ }
}
function clearDraft(id) {
  try { sessionStorage.removeItem(draftKey(id)); } catch { /* sin almacenamiento */ }
}
const isEmptyAnswer = (v) => {
  if (v === undefined || v === null || v === "") return true;
  if (typeof v === "object") {
    // Para respuestas matemáticas {latex, photo_url}: vacío si ambos faltan.
    if ("latex" in v || "photo_url" in v) return !v.latex && !v.photo_url;
    return Object.keys(v).length === 0;
  }
  return false;
};

// Si algo falla al pintar el cuestionario NO se muestra la pantalla del búho
// dormido: se revisa si el diagnóstico ya quedó guardado (→ lista) o se ofrece
// continuar con las respuestas conservadas en este dispositivo.
function QuizCrashFallback({ onRetry }) {
  const { assignmentId } = useParams();
  const [state, setState] = useState("checking"); // checking | done | open
  useEffect(() => {
    let alive = true;
    api.get("/questionnaire-assignments/me")
      .then(({ data }) => {
        const a = (Array.isArray(data) ? data : []).find((x) => x.id === assignmentId);
        if (alive) setState(a && a.status === "completed" ? "done" : "open");
      })
      .catch(() => { if (alive) setState("open"); });
    return () => { alive = false; };
  }, [assignmentId]);
  if (state === "done") {
    toast.success("Tu diagnóstico ya quedó guardado. ¡Gracias!");
    return <Navigate to="/estudiante/cuestionarios" replace />;
  }
  return (
    <div className="max-w-md mx-auto my-16 jasq-card p-6 text-center space-y-3" data-testid="quiz-crash-fallback">
      <p className="text-4xl" aria-hidden>🦉</p>
      <h2 className="font-display text-xl font-extrabold text-[#1F2547]">Un momento…</h2>
      <p className="text-sm text-[#6E7591]">
        {state === "checking"
          ? "Estamos revisando tu diagnóstico."
          : "No pudimos mostrar esta pantalla. Tus respuestas siguen guardadas en este dispositivo."}
      </p>
      {state === "open" && (
        <button onClick={onRetry} className="jasq-btn-primary w-full" data-testid="quiz-crash-retry">Continuar mi diagnóstico</button>
      )}
    </div>
  );
}

class QuizErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false, n: 0 }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error) { try { console.error("[quiz] error de pantalla", error); } catch { /* noop */ } }
  render() {
    if (this.state.failed) {
      return <QuizCrashFallback onRetry={() => this.setState((s) => ({ failed: false, n: s.n + 1 }))} />;
    }
    return <React.Fragment key={this.state.n}>{this.props.children}</React.Fragment>;
  }
}

export function EstudianteCuestionarioPlay() {
  return (
    <QuizErrorBoundary>
      <QuizPlayInner />
    </QuizErrorBoundary>
  );
}

function QuizPlayInner() {
  const { assignmentId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const theme = useMemo(() => getAgeTheme(user?.age), [user?.age]);

  const [assignment, setAssignment] = useState(null);
  const [template, setTemplate] = useState(null);
  const [blockIdx, setBlockIdx] = useState(() => readDraft(assignmentId).blockIdx || 0);
  const [pageIdx, setPageIdx] = useState(() => readDraft(assignmentId).pageIdx || 0);
  const [answers, setAnswers] = useState(() => readDraft(assignmentId).answers || {});
  const [showWelcome, setShowWelcome] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [finished, setFinished] = useState(false);
  const submittedRef = useRef(false);
  const [mascotMessage, setMascotMessage] = useState("");
  const [mascotVariant, setMascotVariant] = useState("info");
  const [floatingMessage, setFloatingMessage] = useState("");
  const [highlightMissing, setHighlightMissing] = useState(false);

  // ---- Voz del búho (Web Speech API) ----
  const [voiceOn, setVoiceOn] = useState(false);
  useEffect(() => { defaultOwlVoiceForStudents(); setVoiceOn(isOwlVoiceOn()); }, []);
  useEffect(() => () => stopOwl(), []);
  const say = (text) => { if (text) speakOwl(String(text)); };
  const toggleVoice = () => {
    const next = setOwlVoice(!voiceOn);
    setVoiceOn(next);
    if (next) speakOwl(mascotMessage || template?.welcome_message || "¡Vamos a comenzar!", { force: true });
    else stopOwl();
  };

  const isAdult = theme.id === "adult";
  const showMascot = !isAdult;

  useEffect(() => {
    (async () => {
      try {
        const { data: list } = await api.get("/questionnaire-assignments/me");
        const a = list.find((x) => x.id === assignmentId);
        if (!a) { toast.error("Diagnóstico no encontrado"); nav(-1); return; }
        if (a.locked) {
          toast.error(`Este diagnóstico estará disponible el ${formatDateMX(a.available_on || a.scheduled_for)}.`);
          nav("/estudiante/cuestionarios", { replace: true });
          return;
        }
        if (a.status === "completed") {
          toast.success("Este diagnóstico ya fue respondido. ¡Gracias!");
          nav("/estudiante/cuestionarios", { replace: true });
          return;
        }
        setAssignment(a);
        const { data: tpl } = await api.get(`/questionnaire-templates/${a.template_code}`);
        setTemplate(tpl);
        setMascotMessage(tpl.welcome_message || "¡Hola! Vamos a comenzar.");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar");
      }
    })();
  }, [assignmentId, nav]);

  const startNow = async () => {
    // Limpia caché vieja en segundo plano para evitar pantallas en blanco por
    // archivos desactualizados tras un despliegue (transparente para el niño).
    clearAppCaches();
    try { await api.post(`/questionnaire-assignments/${assignmentId}/start`); } catch {}
    setShowWelcome(false);
    const firstMsg = template?.blocks?.[0]?.motivational_phrase;
    if (firstMsg) setMascotMessage(firstMsg);
    // El búho lee el inicio en voz alta (gesto del usuario → permitido).
    say(template?.blocks?.[0]?.intro || firstMsg || template?.welcome_message || "¡Vamos a empezar! Lee con calma y responde con sinceridad.");
  };

  const block = template?.blocks?.[blockIdx];
  const isLastBlock = template ? blockIdx >= (template.blocks?.length || 1) - 1 : false;

  // #interactividad — Niños (5-10) y Adolescentes (11-15) responden de 2 en 2
  // preguntas por pantalla; jóvenes/adultos ven el bloque completo.
  const QPP = (theme.id === "kids" || theme.id === "teen") ? 2 : Infinity;
  const blockQuestions = block?.questions || [];
  const pageCount = Math.max(1, QPP === Infinity ? 1 : Math.ceil(blockQuestions.length / QPP));
  const pageQuestions = QPP === Infinity ? blockQuestions : blockQuestions.slice(pageIdx * QPP, pageIdx * QPP + QPP);
  const isLastPage = pageIdx >= pageCount - 1;
  const showSubmit = isLastBlock && isLastPage;

  const requiredUnanswered = useMemo(() => {
    if (!block) return [];
    // Se valida sólo la página visible para poder avanzar de 2 en 2.
    // v5.6.0 · TODAS las preguntas del CEP y del CAD son obligatorias: no se avanza sin responder.
    return pageQuestions.filter((q) => isEmptyAnswer(answers[q.id]));
  }, [block, answers, pageQuestions]);

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  // Borrador local: si la pantalla se recarga a mitad del diagnóstico, se conserva el avance.
  useEffect(() => {
    if (finished || submittedRef.current) return;
    if (Object.keys(answers).length === 0 && blockIdx === 0 && pageIdx === 0) return;
    writeDraft(assignmentId, { answers, blockIdx, pageIdx });
  }, [answers, blockIdx, pageIdx, finished, assignmentId]);

  // ---- Aviso al abandonar evaluación inconclusa (#17) ----
  const hasAnswers = Object.keys(answers).length > 0;
  const inProgress = !showWelcome && template && hasAnswers;
  useEffect(() => {
    if (!inProgress) return;
    const handler = (e) => {
      e.preventDefault();
      // Mensaje del navegador (los navegadores modernos muestran su propio
      // texto, pero la barra de animo aparece como toast antes de ese diálogo
      // si el usuario presiona refresh/cerrar).
      e.returnValue = "¡No te rindas! Si sales perderás tu avance. ¡Termina el diagnóstico, tú puedes!";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [inProgress]);

  // Bloquea la navegación interna (volver, ir al inicio, etc.).
  const guardedExit = (action) => {
    if (!inProgress) { action(); return; }
    const confirmExit = window.confirm(
      "💛 ¡Espera! Estás a mitad del diagnóstico.\n\n" +
      "Si sales ahora perderás todo tu avance.\n\n" +
      "¡Ya estás muy cerca! Cada respuesta cuenta y nos ayuda a conocerte mejor.\n\n" +
      "¿Realmente quieres salir? (Aceptar = salir · Cancelar = seguir)"
    );
    if (confirmExit) action();
  };

  const goNext = () => {
    if (requiredUnanswered.length > 0) {
      const first = requiredUnanswered[0];
      setHighlightMissing(true);
      setMascotMessage(`Te falta una pregunta: "${first.text.slice(0, 60)}${first.text.length > 60 ? "…" : ""}".`);
      setMascotVariant("warn");
      setFloatingMessage(`Te falta${requiredUnanswered.length > 1 ? "n" : ""} ${requiredUnanswered.length} pregunta${requiredUnanswered.length > 1 ? "s" : ""}.`);
      say(`Te falta${requiredUnanswered.length > 1 ? "n" : ""} ${requiredUnanswered.length} pregunta${requiredUnanswered.length > 1 ? "s" : ""}. Tú puedes, vamos a completarla${requiredUnanswered.length > 1 ? "s" : ""}.`);
      const el = document.getElementById(`q-${first.id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setHighlightMissing(false);
    // Avanzar dentro del bloque (de 2 en 2) antes de pasar al siguiente bloque.
    if (!isLastPage) {
      setPageIdx((p) => p + 1);
      const cheer = theme.id === "kids"
        ? ["¡Muy bien! 🌟", "¡Vas genial!", "¡Sigue así! 🚀"]
        : theme.id === "teen"
          ? ["¡Avanzando!", "Buen ritmo.", "Sigamos."]
          : ["Continuemos."];
      setMascotVariant("success");
      setMascotMessage(cheer[pageIdx % cheer.length]);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (isLastBlock) return submit();
    const nextIdx = blockIdx + 1;
    setBlockIdx(nextIdx);
    setPageIdx(0);
    const phrases = theme.id === "kids"
      ? ["¡Eres increíble!", "¡Genial, sigamos!", "¡Vas como un cohete! 🚀", "¡Qué bien lo haces!"]
      : theme.id === "teen"
        ? ["¡Avanzando!", "Excelente progreso.", "Sigues firme."]
        : ["Avanzando.", "Buen progreso.", "Continuemos."];
    setMascotMessage(template.blocks[nextIdx]?.motivational_phrase || phrases[nextIdx % phrases.length]);
    setMascotVariant("success");
    say(template.blocks[nextIdx]?.intro || template.blocks[nextIdx]?.motivational_phrase || phrases[nextIdx % phrases.length]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrev = () => {
    setHighlightMissing(false);
    if (pageIdx > 0) {
      setPageIdx((p) => p - 1);
      setMascotVariant("info");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (blockIdx === 0) return;
    const prevIdx = blockIdx - 1;
    const prevQs = template.blocks[prevIdx]?.questions?.length || 0;
    const prevPages = QPP === Infinity ? 1 : Math.max(1, Math.ceil(prevQs / QPP));
    setBlockIdx(prevIdx);
    setPageIdx(prevPages - 1);
    setMascotVariant("info");
    setMascotMessage(template.blocks[prevIdx]?.motivational_phrase || "Revisemos lo anterior.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (submittedRef.current || submitting) return; // evita doble envío
    // v5.6.0 · revisión final: si alguna pregunta de cualquier bloque quedó sin responder (p. ej. borrador antiguo), se vuelve a ella.
    for (let bi = 0; bi < (template?.blocks?.length || 0); bi += 1) {
      const qs = template.blocks[bi]?.questions || [];
      const qi = qs.findIndex((q) => isEmptyAnswer(answers[q.id]));
      if (qi >= 0) {
        setBlockIdx(bi);
        setPageIdx(QPP === Infinity ? 0 : Math.floor(qi / QPP));
        setHighlightMissing(true);
        setMascotVariant("warn");
        setMascotMessage("Todavía hay una pregunta sin responder. Todas son obligatorias.");
        setFloatingMessage("Falta una pregunta por responder.");
        setTimeout(() => document.getElementById(`q-${qs[qi].id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers).map(([question_id, value]) => ({ question_id, value })),
      };
      const { data } = await api.post(`/questionnaire-assignments/${assignmentId}/submit`, payload);
      submittedRef.current = true;
      clearDraft(assignmentId);
      stopOwl();
      const farewell = template?.farewell_message || (theme.id === "kids" ? "¡Lo lograste! 🎉" : "Diagnóstico enviado · gracias por participar");
      toast.success(farewell, { duration: 5000 });
      // Se reemplaza toda la pantalla por un aviso simple y se navega después,
      // para no desmontar el cuestionario mientras aún está animándose.
      setFinished(true);
      setTimeout(() => nav("/estudiante/cuestionarios", { replace: true, state: { justFinished: data, farewell } }), 900);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 400 && /ya respondida|completado/i.test(String(detail || ""))) {
        submittedRef.current = true;
        clearDraft(assignmentId);
        toast.success("Tu diagnóstico ya estaba guardado. ¡Gracias!");
        nav("/estudiante/cuestionarios", { replace: true });
        return;
      }
      toast.error(detail || "No se pudo enviar. Revisa tu conexión y vuelve a tocar el botón; tus respuestas siguen aquí.");
      setSubmitting(false);
    }
  };

  const totalQs = useMemo(() => (template?.blocks || []).reduce((acc, b) => acc + (b.questions?.length || 0), 0), [template]);
  const answeredQs = Object.keys(answers).filter((k) => answers[k] !== undefined && answers[k] !== "").length;
  const pct = totalQs ? Math.round((answeredQs / totalQs) * 100) : 0;

  if (!template) {
    return <div className="p-10 text-center text-[#6E7591]">Cargando…</div>;
  }

  if (finished) {
    return (
      <div className="max-w-md mx-auto my-20 jasq-card p-8 text-center space-y-2" data-testid="quiz-finished">
        <p className="text-5xl" aria-hidden>🎉</p>
        <h2 className="font-display text-2xl font-extrabold text-[#1F2547]">¡Diagnóstico guardado!</h2>
        <p className="text-sm text-[#6E7591]">Un momento, te llevamos a tus diagnósticos…</p>
      </div>
    );
  }

  // ===== Welcome =====
  if (showWelcome) {
    return (
      <div data-testid="quiz-welcome">
        <QuizBackground scene={template.background_scene} theme={theme} />
        <div className="relative z-[1] max-w-5xl mx-auto py-8 space-y-6">
        <button
          type="button"
          onClick={() => nav("/estudiante/cuestionarios")}
          data-testid="quiz-welcome-back"
          className="inline-flex items-center gap-1.5 text-sm font-extrabold text-white bg-[#1F2547] hover:bg-[#A43790] px-4 py-2.5 rounded-full shadow-lg border-2 border-white/30 transition-colors"
        >
          <ArrowLeft size={16} /> Volver a mis diagnósticos
        </button>
        <div className={`${theme.cardRadius} ${theme.cardBorder} ${theme.cardShadow} bg-gradient-to-br ${theme.id === "adult" ? "from-white to-white" : theme.headerGradient} p-8 space-y-5`}>
          <div className="flex items-center gap-2">
            <span className="text-white font-extrabold text-xs rounded-full px-3 py-1 bg-[#A43790]">{template.code}</span>
            <span className="text-xs uppercase tracking-wider font-bold text-[#6E7591]">{template.stage || ""}</span>
          </div>
          <h1 className={`font-display ${theme.id === "kids" ? "text-4xl md:text-5xl" : "text-3xl md:text-4xl"} ${theme.fontWeight} text-[#1F2547] leading-tight`}>
            {template.title}
          </h1>

          {showMascot && (
            <div className="space-y-2">
              <Mascot message={template.welcome_message || "¡Hola!"} variant="info" testid="mascot-welcome" size={theme.mascotSize} />
              {owlVoiceSupported() && (
                <button
                  type="button"
                  onClick={toggleVoice}
                  data-testid="quiz-voice-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-bold border-2 transition-colors ${
                    voiceOn ? "bg-[#A43790] text-white border-[#A43790]" : "bg-white text-[#4C5478] border-[#E1E5F0] hover:border-[#A43790]"
                  }`}
                >
                  {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
                  {voiceOn ? "Zarek te leerá en voz alta" : "Activar voz de Zarek"}
                </button>
              )}
            </div>
          )}

          <div className={`${theme.questionRadius} ${theme.id === "adult" ? "bg-[#F7F8FA] border border-[#E1E5F0]" : "bg-white border-4 border-[#FFCC28] shadow-inner"} p-5 text-base text-[#1F2547]`}>
            <p className={`${theme.fontWeight} mb-3 ${theme.id === "kids" ? "text-lg" : "text-base"} flex items-center gap-2`}>
              {!isAdult && <Sparkles size={18} className="text-[#FFCC28]" />}
              {theme.welcomeIntroTitle}
            </p>
            {template.intro && (
              <p className="text-base text-[#1F2547] mb-3 whitespace-pre-line [overflow-wrap:anywhere] leading-relaxed" data-testid="quiz-intro">
                {template.intro}
              </p>
            )}
            <ul className="space-y-2 text-base">
              {(Array.isArray(template.welcome_bullets) && template.welcome_bullets.filter(Boolean).length > 0
                ? template.welcome_bullets.filter(Boolean).map((t) => ({ e: theme.id === "kids" ? "💡" : "✨", t }))
                : theme.welcomeBullets
              ).map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className={theme.id === "kids" ? "text-xl" : ""}>{b.e}</span>
                  <span className="whitespace-pre-line [overflow-wrap:anywhere]">{b.t}</span>
                </li>
              ))}
              <li className="flex items-start gap-2">
                <span className={theme.id === "kids" ? "text-xl" : ""}>{theme.id === "kids" ? "📊" : "→"}</span>
                <span>{template.blocks.length} bloques · {totalQs} preguntas.</span>
              </li>
            </ul>
          </div>

          {template.important_note && (
            <div
              className="rounded-2xl border-2 border-[#F5B82E] bg-[#FFF7DA] px-4 py-3 flex items-start gap-2.5"
              data-testid="quiz-important-note"
            >
              <AlertCircle size={18} className="text-[#9C7A18] shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#9C7A18]">Nota importante</p>
                <p className="text-sm font-bold text-[#1F2547] mt-0.5 whitespace-pre-line [overflow-wrap:anywhere]">{template.important_note}</p>
              </div>
            </div>
          )}

          <button onClick={startNow}
            data-testid="start-quiz"
            className={`w-full ${theme.questionRadius} bg-gradient-to-r ${theme.primaryBtnGradient} text-white ${theme.fontWeight} ${theme.id === "kids" ? "text-xl py-4 border-4 border-[#1F2547] shadow-[6px_6px_0_#1F2547]" : "text-base py-3 border-2 border-[#1F2547]"} inline-flex items-center justify-center gap-3`}>
            <Rocket size={20} /> {theme.startLabel}
          </button>
        </div>
        </div>
      </div>
    );
  }

  // ===== Quiz running =====
  return (
    <div data-testid="quiz-play">
      <QuizBackground scene={template.background_scene} theme={theme} />
      <div className="relative z-[1] max-w-5xl mx-auto py-4 px-4 space-y-5 pb-32">
      {/* Header progreso */}
      <div className={`sticky top-0 z-20 bg-white/95 backdrop-blur ${theme.questionRadius} ${theme.id === "kids" ? "border-4" : "border-2"} border-[#1F2547] p-3 shadow-md`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="rounded-xl bg-[#A43790] text-white font-extrabold px-3 py-1 text-sm">
              Bloque {blockIdx + 1}<span className="opacity-70">/{template.blocks.length}</span>
            </span>
            <span className="text-xs font-bold text-[#6E7591]">{answeredQs}/{totalQs}</span>
          </div>
          {theme.blockProgressStyle === "emoji" && (
            <div className="flex items-center gap-1.5 text-xl">
              {Array.from({ length: template.blocks.length }).map((_, i) => (
                <span key={i} className={`transition-all ${i === blockIdx ? "scale-125" : i > blockIdx ? "grayscale opacity-30" : ""}`}>
                  {i < blockIdx ? "✅" : i === blockIdx ? "🎯" : "⚪"}
                </span>
              ))}
            </div>
          )}
          {theme.blockProgressStyle === "dots" && (
            <div className="flex items-center gap-1">
              {Array.from({ length: template.blocks.length }).map((_, i) => (
                <span key={i} className={`w-2.5 h-2.5 rounded-full transition-all ${i < blockIdx ? "bg-[#3FA84D]" : i === blockIdx ? "bg-[#A43790] scale-125" : "bg-[#E1E5F0]"}`} />
              ))}
            </div>
          )}
          {(theme.blockProgressStyle === "bar" || theme.blockProgressStyle === "text") && (
            <span className="text-xs font-bold text-[#6E7591]">{pct}%</span>
          )}
          <button
            type="button"
            onClick={() => guardedExit(() => nav("/estudiante/cuestionarios"))}
            data-testid="quiz-exit-btn"
            title="Salir del diagnóstico"
            className="ml-auto text-xs font-bold text-[#A52258] hover:text-[#1F2547] inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-[#FFE9F0]"
          >
            ✕ Salir
          </button>
        </div>
        <div className={`h-${theme.id === "kids" ? "3" : "2"} mt-2 bg-[#F2F4FA] rounded-full overflow-hidden ${theme.id === "kids" ? "border-2 border-[#1F2547]" : ""}`}>
          <motion.div
            className="h-full bg-gradient-to-r from-[#FFCC28] via-[#D63384] to-[#2C49A0]"
            animate={{ width: `${pct}%` }}
            transition={{ type: "spring", stiffness: 80, damping: 18 }}
          />
        </div>
      </div>

      {showMascot && <Mascot message={mascotMessage} variant={mascotVariant} testid="mascot-quiz" size={theme.mascotSize} />}

      {/* Bloque actual */}
      <motion.div
        key={`${blockIdx}-${pageIdx}`}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
        className={`${theme.cardRadius} ${theme.cardBorder} ${theme.cardShadow} bg-white p-6 space-y-5`}
      >
        <div>
          <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">
            Bloque {blockIdx + 1}
            {pageCount > 1 && <span className="text-[#6E7591]"> · Parte {pageIdx + 1}/{pageCount}</span>}
          </p>
          <h2 className={`font-display ${theme.id === "kids" ? "text-2xl md:text-3xl" : theme.id === "adult" ? "text-xl" : "text-2xl"} ${theme.fontWeight} text-[#1F2547] leading-tight`}>
            {block.title}
          </h2>
          {pageIdx === 0 && block.motivational_phrase && (
            <div className={`mt-3 inline-block ${theme.motivationStyle}`}>
              <p className={`${theme.id === "kids" ? "text-sm font-bold" : "text-xs"} text-[#1F2547] whitespace-pre-line`}>
                {theme.id === "kids" ? "💛 " : ""}{block.motivational_phrase}
              </p>
            </div>
          )}
          {pageIdx === 0 && block.intro && (
            <div
              className={`mt-4 ${theme.id === "kids" ? "bg-[#FFF7DA] border-[#F5B82E]" : "bg-[#F7F8FA] border-[#A43790]"} border-l-4 rounded-r-xl rounded-l-md p-4`}
              data-testid="block-intro"
            >
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790] mb-1.5">Lectura del bloque</p>
              <p className={`whitespace-pre-line [overflow-wrap:anywhere] ${theme.id === "kids" ? "text-base font-bold leading-relaxed" : "text-sm leading-relaxed"} text-[#1F2547] italic`}>
                {block.intro}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-5">
          {pageQuestions.map((q, qi) => (
            <QuestionRender
              key={q.id}
              q={q}
              idx={(QPP === Infinity ? 0 : pageIdx * QPP) + qi + 1}
              value={answers[q.id]}
              onChange={(v) => setAnswer(q.id, v)}
              unanswered={highlightMissing && requiredUnanswered.some((u) => u.id === q.id)}
              theme={theme}
              assignmentId={assignmentId}
            />
          ))}
        </div>
      </motion.div>

      {/* Action bar */}
      <div className={`fixed bottom-0 left-0 right-0 z-20 bg-white/95 backdrop-blur ${theme.id === "kids" ? "border-t-4" : "border-t-2"} border-[#1F2547] p-3 shadow-2xl`}>
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button onClick={goPrev} disabled={blockIdx === 0 && pageIdx === 0}
            data-testid="quiz-prev"
            className={`${theme.questionRadius} ${theme.id === "kids" ? "border-4" : "border-2"} border-[#1F2547] bg-white text-[#1F2547] ${theme.fontWeight} px-4 py-3 inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed`}>
            <ArrowLeft size={16} /> <span className="hidden sm:inline">Atrás</span>
          </button>
          <div className="flex-1 text-center">
            <p className="text-xs font-bold text-[#6E7591]">{answeredQs}/{totalQs} • {pct}%</p>
          </div>
          {!showSubmit && (
            <button onClick={goNext}
              data-testid="quiz-next"
              className={`${theme.questionRadius} bg-gradient-to-r ${theme.primaryBtnGradient} ${theme.id === "kids" ? "text-[#1F2547]" : "text-white"} ${theme.fontWeight} px-5 py-3 inline-flex items-center gap-2 ${theme.id === "kids" ? "border-4 border-[#1F2547] shadow-[4px_4px_0_#1F2547]" : "border-2 border-[#1F2547]"}`}>
              {theme.nextLabel} <ArrowRight size={16} />
            </button>
          )}
          {showSubmit && (
            <button onClick={goNext} disabled={submitting}
              data-testid="quiz-submit"
              className={`${theme.questionRadius} bg-gradient-to-r ${theme.submitBtnGradient} text-white ${theme.fontWeight} px-5 py-3 inline-flex items-center gap-2 ${theme.id === "kids" ? "border-4 border-[#1F2547] shadow-[4px_4px_0_#1F2547]" : "border-2 border-[#1F2547]"}`}>
              {submitting ? "Enviando…" : <>{theme.submitLabel} {theme.id === "kids" && <PartyPopper size={16} />}</>}
            </button>
          )}
        </div>
      </div>

      {floatingMessage && (
        <FloatingOwl
          key={floatingMessage}
          message={floatingMessage}
          variant={mascotVariant === "warn" ? "warn" : "party"}
          duration={2500}
          onDone={() => setFloatingMessage("")}
        />
      )}
      </div>
    </div>
  );
}

// ----------------- Pregunta individual (adaptativa) -----------------
function QuestionRender({ q, idx, value, onChange, unanswered, theme, assignmentId }) {
  const id = `q-${q.id}`;

  // Para preguntas tipo "likert" sin variante explícita, asumimos "likert_agree".
  const effectiveScale = q.scale || (q.type === "likert" ? "likert_agree" : (q.type === "scale_1_10" ? "scale_1_10" : null));
  const customLabels = q.scale_labels || {};

  const useFaces = theme.optsLayout === "faces" && q.type === "likert" &&
    (effectiveScale === "likert_agree" || effectiveScale === "likert_freq");
  const useChips = theme.optsLayout === "chips" && q.type === "likert" &&
    (effectiveScale === "likert_agree" || effectiveScale === "likert_freq");
  const useTable = theme.optsLayout === "table" && q.type === "likert" &&
    (effectiveScale === "likert_agree" || effectiveScale === "likert_freq");
  const useScale10 = q.type === "scale_1_10" || effectiveScale === "scale_1_10";

  const baseFaces = effectiveScale === "likert_freq" ? theme.facesFreq : theme.facesAgree;
  const baseAdult = effectiveScale === "likert_freq" ? theme.adultFreq : theme.adultAgree;
  // Aplica etiquetas personalizadas configuradas por el admin (extremos + centro).
  const applyCustomLabels = (arr) => {
    if (!arr) return arr;
    const last = arr.length - 1;
    const mid = Math.floor((arr.length - 1) / 2);
    return arr.map((item, i) => {
      if (i === 0 && customLabels.min) return { ...item, label: customLabels.min };
      if (i === last && customLabels.max) return { ...item, label: customLabels.max };
      if (i === mid && customLabels.mid) return { ...item, label: customLabels.mid };
      return item;
    });
  };
  const facesData = applyCustomLabels(baseFaces);
  const adultData = applyCustomLabels(baseAdult);

  const bgClass = unanswered
    ? "border-[#D63384] bg-[#FFE9F0]"
    : (value !== undefined && value !== "")
      ? "border-[#3FA84D]/60 bg-[#F4FBEF]"
      : `border-[#E1E5F0] bg-white`;

  return (
    <motion.div
      id={id}
      initial={false}
      animate={unanswered ? { x: [-6, 6, -4, 4, 0] } : {}}
      transition={{ duration: 0.5 }}
      className={`${theme.questionRadius} ${theme.questionBorder} ${theme.questionPadding} transition-all ${bgClass}`}
      data-testid={`question-${q.id}`}
    >
      <p className={`${theme.questionTextSize} ${theme.fontWeight} text-[#1F2547] mb-4 leading-snug flex gap-2`}>
        <span className={`shrink-0 inline-flex items-center justify-center ${theme.numberSize} rounded-full bg-[#1F2547] text-white`}>{idx}</span>
        <span><RenderTextWithMath text={q.text} /><span className="text-[#D63384] ml-1">*</span></span>
      </p>

      {/* KIDS / TEEN — caritas grandes */}
      {useFaces && (
        <div className="grid grid-cols-5 gap-2 md:gap-3">
          {facesData.map((o) => {
            const active = value === o.v;
            return (
              <motion.button
                key={o.v} type="button" onClick={() => onChange(o.v)}
                data-testid={`opt-${q.id}-${o.v}`}
                whileTap={{ scale: 0.92 }}
                whileHover={{ scale: 1.05, y: -2 }}
                className={`relative ${theme.optionRadius} ${theme.optionBorder} p-2 sm:p-3 flex flex-col items-center gap-1 transition-all ${
                  active ? `shadow-[4px_4px_0_#1F2547] -translate-y-1` : "border-[#E1E5F0] bg-white hover:border-[#FFCC28]"
                }`}
                style={active ? { borderColor: "#1F2547", background: o.bg } : {}}
              >
                <span className={`${theme.faceSize} select-none`} aria-hidden>{o.face}</span>
                <span className={`text-[10px] sm:text-xs ${theme.fontWeight} leading-tight text-center ${active ? "" : "text-[#4C5478]"}`}
                  style={active ? { color: o.color } : {}}>{o.label}</span>
                {active && (
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#3FA84D] text-white flex items-center justify-center border-2 border-white shadow-md">
                    <CheckCircle2 size={14} />
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>
      )}

      {/* YOUNG — chips minimalistas */}
      {useChips && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {facesData.map((o) => {
            const active = value === o.v;
            return (
              <button key={o.v} type="button" onClick={() => onChange(o.v)}
                data-testid={`opt-${q.id}-${o.v}`}
                className={`px-3 py-2.5 ${theme.optionRadius} border-2 text-left transition-all ${
                  active ? "border-[#1F2547] bg-[#1F2547] text-white shadow-md" : "border-[#E1E5F0] hover:border-[#2C49A0] text-[#1F2547]"
                }`}>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex w-6 h-6 rounded-full items-center justify-center text-xs font-bold ${active ? "bg-white text-[#1F2547]" : "bg-[#F2F4FA] text-[#1F2547]"}`}>{o.v}</span>
                  <span className="text-xs font-bold leading-tight">{o.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ADULT — tabla formal con radio buttons */}
      {useTable && (
        <div className="grid grid-cols-5 gap-1 text-center">
          {adultData.map((o) => {
            const active = value === o.v;
            return (
              <button key={o.v} type="button" onClick={() => onChange(o.v)}
                data-testid={`opt-${q.id}-${o.v}`}
                className={`px-2 py-2 ${theme.optionRadius} border-2 transition-colors ${
                  active ? "border-[#1F2547] bg-[#1F2547] text-white" : "border-[#E1E5F0] hover:border-[#2C49A0] text-[#4C5478]"
                }`}>
                <div className={`w-5 h-5 mx-auto mb-1 rounded-full border-2 ${active ? "border-white bg-white" : "border-[#6E7591]"}`}>
                  {active && <div className="w-2 h-2 rounded-full bg-[#1F2547] mx-auto mt-[5px]"/>}
                </div>
                <span className="text-[10px] leading-tight font-bold">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* 1..10 escala (mismo para todos pero adapta colores) */}
      {useScale10 && (
        <div>
          <div className="flex justify-between text-[10px] font-bold text-[#6E7591] mb-1 px-1">
            <span>{customLabels.min || theme.scoreLabels1to10.left}</span>
            <span>{customLabels.max || theme.scoreLabels1to10.right}</span>
          </div>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((v) => {
              const active = value === v;
              const hue = ["#2C49A0","#3766B6","#5B85C5","#85A8D5","#C5B560","#FFCC28","#FFA200","#E66F2B","#C84A4F","#A43790"][v - 1];
              return (
                <motion.button key={v} type="button"
                  whileTap={{ scale: 0.9 }} whileHover={{ scale: 1.08 }}
                  onClick={() => onChange(v)} data-testid={`opt-${q.id}-${v}`}
                  className={`aspect-square ${theme.optionRadius} ${theme.optionBorder} ${theme.fontWeight} transition-all ${
                    active ? "border-[#1F2547] text-white shadow-md scale-110" : "border-[#E1E5F0] text-[#1F2547] bg-white hover:border-[#1F2547]"
                  }`}
                  style={active ? { background: hue } : {}}>{v}</motion.button>
              );
            })}
          </div>
        </div>
      )}

      {/* Imagen de contexto: se muestra encima de la respuesta cuando NO es interactiva */}
      {q.image_url && q.type !== "image_interactive" && (
        <img
          src={q.image_url}
          alt=""
          data-testid={`q-context-image-${q.id}`}
          className="mb-3 max-h-72 rounded-2xl border-2 border-[#E1E5F0] bg-white shadow-sm"
        />
      )}

      {q.type === "numeric" && (
        <input type="number" inputMode="decimal"
          className={`max-w-[200px] px-4 py-3 ${theme.optionRadius} ${theme.optionBorder} border-[#E1E5F0] ${theme.id === "kids" ? "text-2xl text-center" : "text-base"} ${theme.fontWeight} text-[#1F2547] outline-none focus:border-[#A43790]`}
          value={value ?? ""}
          onChange={(e) => { if (/^-?\d*\.?\d*$/.test(e.target.value)) onChange(e.target.value); }}
          onKeyDown={(e) => { if (["e", "E", "+"].includes(e.key)) e.preventDefault(); }}
          onPaste={(e) => { const t = e.clipboardData.getData("text"); if (!/^-?\d*\.?\d*$/.test(t)) e.preventDefault(); }}
          placeholder={theme.id === "kids" ? "?" : "Escribe…"}
          data-testid={`opt-${q.id}-num`} />
      )}

      {q.type === "fill_in_blank" && (() => {
        const fmt = q.answer_format || "text";
        const filterFor = {
          numeric: (v) => v.replace(/[^0-9.\-]/g, ""),
          alphanumeric: (v) => v.replace(/[^a-zA-Z0-9 ]/g, ""),
          text: (v) => v,
        }[fmt];
        return (
          <input type="text"
            className={`max-w-[280px] px-4 py-3 ${theme.optionRadius} ${theme.optionBorder} border-[#E1E5F0] text-base ${theme.fontWeight} text-[#1F2547] outline-none focus:border-[#A43790]`}
            value={value ?? ""} onChange={(e) => onChange(filterFor(e.target.value))}
            placeholder={fmt === "numeric" ? "Escribe un número…" : "Escribe la respuesta…"}
            inputMode={fmt === "numeric" ? "decimal" : "text"}
            data-testid={`opt-${q.id}-text`} />
        );
      })()}

      {q.type === "stacked_op" && Array.isArray(q.operands) && (
        <div className="flex flex-col items-center gap-3 py-2">
          <StackedOperation
            operands={q.operands}
            operator={q.operator || "+"}
            size={theme.id === "kids" ? "lg" : "md"}
            value={value ?? ""}
            onChange={onChange}
            testId={`opt-${q.id}-stacked`}
          />
          <p className="text-[10px] text-[#6E7591] uppercase tracking-wider font-bold">Escribe tu respuesta debajo de la línea</p>
        </div>
      )}

      {q.type === "equation" && (
        <div className="space-y-2">
          {q.equation_template && (
            <p className="text-sm text-[#4C5478] font-mono bg-[#F7F8FA] rounded px-3 py-2 border border-[#E1E5F0]">
              {q.equation_template}
            </p>
          )}
          <StudentMathResponse
            value={value}
            onChange={onChange}
            assignmentId={assignmentId}
            questionId={q.id}
            placeholder="Escribe tu respuesta en LaTeX. Ej: x = 5"
          />
        </div>
      )}

      {q.type === "multiple_choice" && (
        <div className="space-y-2 w-full max-w-3xl">
          {(q.options || []).map((o, idx) => {
            const active = value === o.value;
            return (
              <button key={idx} type="button"
                onClick={() => onChange(o.value)}
                data-testid={`opt-${q.id}-mc-${idx}`}
                className={`block w-full text-left px-4 py-3 ${theme.optionRadius} ${theme.optionBorder} ${active ? "border-[#A43790] bg-[#FFF1F8] text-[#1F2547]" : "border-[#E1E5F0] hover:border-[#A43790] bg-white text-[#4C5478]"} ${theme.fontWeight} text-sm transition-colors`}>
                <span className="inline-block w-6 h-6 rounded-full bg-[#1F2547] text-white text-xs font-extrabold text-center leading-6 mr-2">
                  {String.fromCharCode(65 + idx)}
                </span>
                <RenderTextWithMath text={o.label || ""} />
              </button>
            );
          })}
        </div>
      )}

      {q.type === "image_interactive" && q.image_url && (
        <ImageInteractiveCanvas
          imageUrl={q.image_url}
          drawingTools={q.drawing_tools && q.drawing_tools.length ? q.drawing_tools : ["brush-red", "eraser"]}
          value={value}
          onChange={onChange}
        />
      )}

      {q.type === "open_text" && (
        <textarea rows={3}
          className={`w-full px-4 py-3 ${theme.optionRadius} ${theme.optionBorder} border-[#E1E5F0] text-base text-[#1F2547] outline-none focus:border-[#A43790]`}
          placeholder="Escribe tu respuesta…"
          value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          data-testid={`opt-${q.id}-open2`} />
      )}

      {q.type === "open" && (
        <textarea rows={3}
          className={`w-full px-4 py-3 ${theme.optionRadius} ${theme.optionBorder} border-[#E1E5F0] ${theme.id === "kids" ? "text-base" : "text-sm"} text-[#1F2547] outline-none focus:border-[#A43790]`}
          placeholder={theme.id === "kids" ? "Cuéntame con tus propias palabras…" : "Escribe tu respuesta…"}
          value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          data-testid={`opt-${q.id}-open`} />
      )}

      {q.type === "multi_part" && (
        <MultiPartPlayer
          q={q}
          value={value || {}}
          onChange={onChange}
          theme={theme}
        />
      )}

      {q.type === "table_input" && (
        <TablePlayer q={q} value={value || {}} onChange={onChange} />
      )}

      {value !== undefined && value !== "" && (
        <button type="button" onClick={() => onChange(undefined)}
          className="mt-3 text-[10px] uppercase tracking-wider font-bold text-[#6E7591] hover:text-[#A43790]"
          data-testid={`clear-${q.id}`}>↺ borrar</button>
      )}
    </motion.div>
  );
}

export default EstudianteCuestionarios;

// Mini toolbar de símbolos matemáticos para preguntas de tipo "equation".
const MATH_SYMBOLS_STUDENT = ["+", "−", "×", "÷", "=", "√", "²", "³", "π", "()", "x", "y"];
function MathInsertBar({ onInsert }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid="student-math-bar">
      {MATH_SYMBOLS_STUDENT.map((s) => (
        <button key={s} type="button" onClick={() => onInsert(s)}
          className="w-7 h-7 rounded border border-[#E1E5F0] hover:bg-[#FFF7DA] text-sm font-bold text-[#1F2547]" title={s}>{s}</button>
      ))}
    </div>
  );
}


/**
 * MultiPartPlayer
 * ---------------
 * Renderiza una pregunta multi-parte para el alumno. Cada sub-pregunta
 * tiene su propio tipo (multiple_choice / numeric / open_text / fill_in_blank).
 * La respuesta se acumula en un objeto { [sub_id]: valor }.
 */
function MultiPartPlayer({ q, value, onChange, theme }) {
  const subs = q.sub_questions || [];
  if (subs.length === 0) {
    return (
      <p className="text-xs italic text-[#A52258] bg-[#FFE9F0] rounded p-2">
        Esta pregunta no tiene sub-preguntas configuradas. Avisa a tu profesor/a.
      </p>
    );
  }
  const setSub = (subId, v) => {
    const next = { ...(value || {}) };
    if (v === undefined || v === "" || v === null) delete next[subId];
    else next[subId] = v;
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid={`mp-${q.id}-player`}>
      {subs.map((s, i) => {
        const label = s.label || String.fromCharCode(65 + i);
        const cur = (value || {})[s.id];
        return (
          <div
            key={s.id || i}
            className={`rounded-2xl border-2 border-[#E1E5F0] bg-white p-3 sm:p-4 space-y-2`}
            data-testid={`mp-${q.id}-sub-${i}`}
          >
            <div className="flex items-start gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#A43790] text-white text-xs font-extrabold shrink-0 mt-0.5">{label}</span>
              <p className={`flex-1 ${theme.id === "kids" ? "text-base" : "text-sm"} font-bold text-[#1F2547]`}>{s.text || `Sub-pregunta ${label}`}</p>
            </div>

            {s.type === "multiple_choice" && (
              <div className="flex flex-col gap-1.5 pl-9">
                {(s.options || []).map((o, oi) => {
                  const checked = cur === o.value;
                  return (
                    <button
                      key={oi}
                      type="button"
                      onClick={() => setSub(s.id, o.value)}
                      data-testid={`mp-${q.id}-sub-${i}-opt-${oi}`}
                      className={`text-left px-3 py-2 rounded-xl border-2 text-sm transition-colors ${
                        checked
                          ? "border-[#A43790] bg-[#FFE9F0] text-[#A52258] font-bold"
                          : "border-[#E1E5F0] hover:border-[#A43790] text-[#1F2547]"
                      }`}
                    >
                      <span className="inline-block w-5 h-5 rounded-full border-2 mr-2 align-middle"
                        style={{
                          borderColor: checked ? "#A43790" : "#A6ACC1",
                          background: checked ? "#A43790" : "transparent",
                        }} />
                      <RenderTextWithMath text={o.label || ""} />
                    </button>
                  );
                })}
              </div>
            )}

            {(s.type === "numeric" || s.type === "fill_in_blank") && (
              <input
                type={s.type === "numeric" ? "number" : "text"}
                step="any"
                inputMode={s.type === "numeric" ? "decimal" : "text"}
                value={cur ?? ""}
                onChange={(e) => setSub(s.id, e.target.value)}
                placeholder={s.type === "numeric" ? "Tu respuesta numérica…" : "Tu respuesta…"}
                className="ml-9 w-[calc(100%-2.25rem)] px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-base outline-none focus:border-[#A43790]"
                data-testid={`mp-${q.id}-sub-${i}-input`}
              />
            )}

            {s.type === "open_text" && (
              <textarea
                rows={2}
                value={cur ?? ""}
                onChange={(e) => setSub(s.id, e.target.value)}
                placeholder="Escribe tu respuesta…"
                maxLength={s.max_length || 500}
                className="ml-9 w-[calc(100%-2.25rem)] px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-sm outline-none focus:border-[#A43790]"
                data-testid={`mp-${q.id}-sub-${i}-text`}
              />
            )}
          </div>
        );
      })}
      <p className="text-[10px] text-[#6E7591] italic text-center">
        Responde todas las partes antes de continuar. Cada parte correcta suma puntos.
      </p>
    </div>
  );
}


